import { ButtonBar, DataGridActionBar, useSnackbar } from "@dain/mui-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SearchButton } from "../../common/button/CommonButtons";
import { CommonTranslKey } from "../../common/CommonTranslKey";
import { PageableResultListDto } from "../../client/pagination/PageableResultListDto";
import {SortingOrderType} from "../../client/pagination/SortingFilterDto";
import {IsdsMessageFilterDto, IsdsMessageInfoDto} from "../../client/message/IsdsMessageApiClient";
import MessageGrid from "./MessageGrid";
import {MessageFilterForm} from "./MessageFilterForm";
import {useClientLocator} from "../../client/ApiClientLocator";
import {PageLoadSize} from "../../common/pagination/PageableGrid";

enum SearchPanelState {
    INITIALIZED,
    IDLE,
    SEARCH_IN_PROGRESS
}

export enum FilterSearchType {
    NEW_SEARCH,
    NEXT_PAGE_SEARCH
}

export interface MessageSearchPanelProps {
    actions?: React.ReactNode;
    onSelectedMessage: (message?: IsdsMessageInfoDto) => void;
    onMessageDoubleClick?: (personId: number) => void;
}

export const DEFAULT_MESSAGE_FILTER: IsdsMessageFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "state.changeTime", order: SortingOrderType.DESC },
        ],
    },
};

export default function MessageSearchPanel(props: Readonly<MessageSearchPanelProps>) {
    const {actions, onSelectedMessage, onMessageDoubleClick} = props;

    const {isdsMessageClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const [panelStatus, setPanelStatus] = useState<SearchPanelState>(SearchPanelState.INITIALIZED);
    const [messageFilter, setMessageFilter] = useState<IsdsMessageFilterDto>(DEFAULT_MESSAGE_FILTER);

    const [messageResult, setMessageResult] = useState<PageableResultListDto<IsdsMessageInfoDto>>({
        items: [],
        nextPageToken: null,
    });

    const [displayedMessagesCount, setDisplayedMessagesCount] = useState<number>(0);
    const [, setSelectedMessage] = useState<IsdsMessageInfoDto>();

    const isInitialized = useRef(false);

    function onFilterChanged(updatedFilter: IsdsMessageFilterDto) {
        setMessageFilter(updatedFilter);
    }

    const updatePaginatedMessage = (newFetchedMessages: PageableResultListDto<IsdsMessageInfoDto>, existingMessagesResults: PageableResultListDto<IsdsMessageInfoDto>, searchType: FilterSearchType):PageableResultListDto<IsdsMessageInfoDto> => {
        if (searchType === FilterSearchType.NEW_SEARCH) {
            return newFetchedMessages;
        } else if (searchType === FilterSearchType.NEXT_PAGE_SEARCH) {
            return {
                items: [...existingMessagesResults.items, ...newFetchedMessages.items],
                nextPageToken: newFetchedMessages.nextPageToken || null
            };
        }

        return {
            items: [],
            nextPageToken: null
        };
    };

    const fetchMessages = useCallback((filter: IsdsMessageFilterDto, searchType: FilterSearchType) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);

            isdsMessageClient.findByFilter(filter)
                .then((filteredMessagesPage) => {
                    const updatedMessages = updatePaginatedMessage(filteredMessagesPage, messageResult, searchType);
                    setMessageResult(updatedMessages);
                })
                .catch((error) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                })
                .finally(() => {
                    setPanelStatus(SearchPanelState.IDLE);
                });
        },
        [isdsMessageClient, snackbar, messageResult, t]
    );

    const countByFilter = useCallback((filter: IsdsMessageFilterDto) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);
            return isdsMessageClient.countByFilter(filter)
                .then((filteredMessagesCount) => {
                    setDisplayedMessagesCount(filteredMessagesCount);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error))
                .finally(() => setPanelStatus(SearchPanelState.IDLE));
        },
        [isdsMessageClient, snackbar, t]
    );

    function resetPagingFilter(messageFilter: IsdsMessageFilterDto, DEFAULT_MESSAGE_FILTER: IsdsMessageFilterDto) {
        messageFilter.paging = DEFAULT_MESSAGE_FILTER.paging;
    }

    const resetAndSearchMessages = useCallback(() => {
            setMessageResult({ items: [], nextPageToken: null });
            setDisplayedMessagesCount(0);

            resetPagingFilter(messageFilter, DEFAULT_MESSAGE_FILTER);

            fetchMessages(messageFilter, FilterSearchType.NEW_SEARCH);
            countByFilter(messageFilter);
        },[fetchMessages, countByFilter, messageFilter],
    );

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            resetAndSearchMessages();
        }
    }, [fetchMessages, countByFilter, resetAndSearchMessages]);

    function onSelectionChange(message: IsdsMessageInfoDto) {
        setSelectedMessage(message);
        onSelectedMessage(message);
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <MessageFilterForm filter={messageFilter} onChange={onFilterChanged} />
            </Grid>
            <Grid item xs={12}>
                <ButtonBar>
                    <SearchButton onClick={resetAndSearchMessages} />
                </ButtonBar>
            </Grid>
            <Grid item xs={12}>
                <Grid container>
                    {actions && (
                        <Grid item xs={12}>
                            <DataGridActionBar actions={actions} />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {panelStatus !== SearchPanelState.INITIALIZED && (
                            <MessageGrid
                                rowsData={messageResult.items}
                                totalMessageCount={displayedMessagesCount}

                                nextPageToken={messageResult.nextPageToken}
                                messageFilter={messageFilter}
                                fetchMessages={fetchMessages}

                                onSelectMessage={onSelectionChange}
                                onMessageDoubleClick={onMessageDoubleClick}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
